import React from 'react';
import { observer } from 'mobx-react';
import { Divider } from 'antd';
import {
    DatabaseFilled,
    EuroOutlined,
    GlobalOutlined,
    InfoCircleOutlined,
    MessageFilled,
    PhoneFilled,
} from '@ant-design/icons';
import { CardBadge } from '../CardBadge';
import cx from 'classnames';
import { IPackage, ITariffPackagesToBuyListItem } from '../../../../interfaces';
import { UnitTypeValues } from '../../../../middleware';
import { showDataWithUnit, showOriginalUnitByType, stringMoney, tAdvancedPlural } from '../../../../instruments';
import { ITrField, tF, tI } from '../../../../translate';
import TariffsStyles from '../../m_styles.less';

const TariffPackageStats = ({
    Icon,
    title,
    value,
    isTitle = false,
}: {
    Icon: React.FC;
    title: ITrField;
    value?: any;
    isTitle?: boolean;
}) => {
    const rawValue = value?.split(' ') || ['0'];

    if (rawValue[0] === '0' && !isTitle) {
        return null;
    }

    return (
        <div className={TariffsStyles.tariffItemPackageViewCardStatsItem}>
            <div className={TariffsStyles.titleContainer}>
                <Icon />
                <span className={TariffsStyles.title}>
                    {tF(title)}
                    {isTitle ? ': ' : ''}
                </span>
            </div>
            <div>
                <span className={TariffsStyles.value}>{value}</span>
            </div>
        </div>
    );
};

interface ITariffItemPackageViewCard {
    packageInfo: IPackage | ITariffPackagesToBuyListItem;
    isClickable?: boolean;
}

export const TariffItemPackageViewCard: React.FC<ITariffItemPackageViewCard> = ({
    packageInfo,
    isClickable = false,
}: ITariffItemPackageViewCard) => {
    const {
        name,
        dataVolume,
        dataVolumeUnit,
        smsVolume,
        voiceVolume,
        operators,
        networks,
        price = 0,
        packageType,
        company,
    } = packageInfo;

    return (
        <div
            className={cx(TariffsStyles.tariffItemPackageViewCard, {
                [TariffsStyles.tariffItemPackageViewCardSelectable]: isClickable,
            })}
        >
            <div className={TariffsStyles.tariffItemPackageViewCardTitle}>
                <h3 className={TariffsStyles.tariffItemPackageViewCardTitleName}>{name}</h3>
                <div>
                    <CardBadge name={packageInfo.packageType?.toUpperCase() || 'LOCAL'} />
                </div>
            </div>
            <div className={TariffsStyles.tariffItemPackageViewCardStats}>
                {packageType === 'extra' && price ? (
                    <TariffPackageStats Icon={EuroOutlined} title={'Price'} value={stringMoney(+price, 'EUR')} />
                ) : null}
                <TariffPackageStats
                    Icon={DatabaseFilled}
                    title={'Data'}
                    value={showDataWithUnit(dataVolume, dataVolumeUnit)}
                />
                <TariffPackageStats
                    Icon={PhoneFilled}
                    title={'Voice'}
                    value={showOriginalUnitByType(`${voiceVolume}`, UnitTypeValues.VOICE)}
                />
                <TariffPackageStats
                    Icon={MessageFilled}
                    title={'SMS'}
                    value={showOriginalUnitByType(`${smsVolume}`, UnitTypeValues.SMS)}
                />
            </div>
            <Divider className={TariffsStyles.tariffItemPackageViewDivider} />
            <div>
                {company && company?.name && (
                    <>
                        <TariffPackageStats Icon={GlobalOutlined} title={'Owner'} isTitle={true} />
                        <div className={TariffsStyles.tariffItemPackageViewCardOperators}>
                            <span className={TariffsStyles.tariffItemPackageViewCardOperatorItem}>
                                {company?.name || ''}
                            </span>
                        </div>
                    </>
                )}
                <TariffPackageStats Icon={GlobalOutlined} title={'Operators'} isTitle={true} />
                <div className={TariffsStyles.tariffItemPackageViewCardOperators}>
                    {operators?.map((value) => {
                        return (
                            <span key={value?.id} className={TariffsStyles.tariffItemPackageViewCardOperatorItem}>
                                {value?.name || `#${value?.id}`}
                            </span>
                        );
                    })}
                </div>
                <TariffPackageStats Icon={GlobalOutlined} title={'Networks'} isTitle={true} />
                <div className={TariffsStyles.tariffItemPackageViewCardOperators}>
                    {networks?.map((value) => {
                        return (
                            <span key={value.id} className={TariffsStyles.tariffItemPackageViewCardOperatorItem}>
                                {value.name}
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

interface ITariffItemPackageView {
    tariffPackages: IPackage[];
}

export const TariffItemPackageView: React.FC<ITariffItemPackageView> = observer(
    ({ tariffPackages }: ITariffItemPackageView) => {
        const roamingPackages = tariffPackages?.filter((p) => p.packageType === 'roaming');
        const localPackages = tariffPackages?.filter((p) => p.packageType === 'local');
        return (
            <div className={TariffsStyles.tariffItemPackageViewContainer}>
                <h2>{tAdvancedPlural('local_package', localPackages?.length)}</h2>
                <div className={TariffsStyles.tariffItemPackageView}>
                    {localPackages?.map((packageInfo) => (
                        <div key={packageInfo.id} className={TariffsStyles.item}>
                            <TariffItemPackageViewCard packageInfo={packageInfo} />
                        </div>
                    ))}
                </div>
                {localPackages?.length <= 0 ? (
                    <div className={TariffsStyles.tariffItemPackageViewNoItems}>
                        <InfoCircleOutlined />
                        <h3>{tI('No Package')}</h3>
                    </div>
                ) : null}
                <h2>{tAdvancedPlural('roaming_package', roamingPackages?.length)}</h2>
                <div className={TariffsStyles.tariffItemPackageView}>
                    {roamingPackages?.map((packageInfo) => (
                        <div key={packageInfo.id} className={TariffsStyles.item}>
                            <TariffItemPackageViewCard packageInfo={packageInfo} />
                        </div>
                    ))}
                    {roamingPackages?.length <= 0 ? (
                        <div className={TariffsStyles.tariffItemPackageViewNoItems}>
                            <InfoCircleOutlined />
                            <h3>{tI('No Package')}</h3>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    },
);
